import React, { useEffect, useState } from "react";
import { FiBell, FiCheck, FiClock, FiFilter, FiShoppingBag, FiCheckCircle, FiXCircle, FiPackage, FiRefreshCw } from "react-icons/fi";
import api from "../../utils/api";
import { toast } from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

const NotificationManagement = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, unread: 0, orders: 0 });

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await api.get("/notifications");
      const notifs = res.data.data || [];
      setNotifications(notifs);

      // Calculate stats
      setStats({
        total: notifs.length,
        unread: notifs.filter(n => !n.isRead).length,
        orders: notifs.filter(n => n.type === 'NEW_ORDER').length
      });
    } catch {
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      // Delete the notification instead of just marking as read
      await api.delete(`/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => n._id !== id));
      toast.success("Notification removed");
    } catch {
      toast.error("Failed to remove notification");
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.patch("/notifications/read-all");
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, isRead: true }))
      );
      toast.success("All notifications marked as read");
      fetchNotifications(); // Refresh to get updated stats
    } catch {
      toast.error("Failed to mark notifications as read");
    }
  };

  const formatTimeAgo = (date) => {
    const diff = Math.floor((Date.now() - new Date(date)) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
    return `${Math.floor(diff / 86400)} day${Math.floor(diff / 86400) > 1 ? 's' : ''} ago`;
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'NEW_ORDER': return <FiShoppingBag />;
      case 'ORDER_ACCEPTED': return <FiCheckCircle />;
      case 'ORDER_CANCELLED': return <FiXCircle />;
      case 'ORDER_COMPLETED': return <FiPackage />;
      default: return <FiBell />;
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      markAsRead(notification._id);
    }
    if (notification.orderId) {
      navigate('/vendor/orders');
    }
  };

  const filteredNotifications = notifications.filter((n) => {
    if (filter === "unread") return !n.isRead;
    if (filter === "orders") return n.type === "NEW_ORDER";
    return true;
  });

  useEffect(() => {
    fetchNotifications();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-4 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 p-3 sm:p-4 md:p-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <FiBell className="text-xl sm:text-2xl text-red-600 w-5 h-5 sm:w-6 sm:h-6" />
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Notifications</h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {stats.unread > 0 && (
              <button
                className="px-3 sm:px-4 py-2 sm:py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
                onClick={markAllAsRead}
                title="Mark all notifications as read"
              >
                <FiCheck className="text-base sm:text-lg" />
                <span className="hidden sm:inline">Mark All Read</span>
                <span className="sm:hidden">All Read</span>
              </button>
            )}
            <button
              className="px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
              onClick={fetchNotifications}
              title="Refresh"
            >
              <FiRefreshCw className="text-base sm:text-lg" />
              <span className="hidden sm:inline">Refresh</span>
              <span className="sm:hidden">↻</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 items-center justify-between mt-4 sm:mt-6">
          <div className="flex flex-wrap gap-2">
            <button
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base ${filter === 'all'
                  ? 'bg-red-500 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              onClick={() => setFilter('all')}
            >
              <span className="hidden sm:inline">All Notifications</span>
              <span className="sm:hidden">All</span>
              <span className="ml-1 sm:ml-2">({stats.total})</span>
            </button>
            <button
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base ${filter === 'unread'
                  ? 'bg-red-500 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              onClick={() => setFilter('unread')}
            >
              <span className="hidden sm:inline">Unread</span>
              <span className="sm:hidden">New</span>
              <span className="ml-1 sm:ml-2">({stats.unread})</span>
            </button>
            <button
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm sm:text-base ${filter === 'orders'
                  ? 'bg-red-500 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              onClick={() => setFilter('orders')}
            >
              <span className="hidden sm:inline">Orders</span>
              <span className="sm:hidden">Orders</span>
              <span className="ml-1 sm:ml-2">({stats.orders})</span>
            </button>
          </div>
        </div>
      </div>

      {/* List Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-gray-200 p-3 sm:p-4">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">
            {filter === 'all' && `All Notifications (${filteredNotifications.length})`}
            {filter === 'unread' && `Unread Notifications (${filteredNotifications.length})`}
            {filter === 'orders' && `Order Notifications (${filteredNotifications.length})`}
          </h2>
        </div>

        {filteredNotifications.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-8 sm:p-12 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiBell className="text-gray-400 text-2xl sm:text-3xl" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">No notifications yet</h3>
            <p className="text-gray-600 text-sm sm:text-base mb-4 sm:mb-6">You're all caught up! New notifications will appear here.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredNotifications.map((n) => (
              <div
                key={n._id}
                className={`p-3 sm:p-4 hover:bg-gray-50 transition-colors duration-200 cursor-pointer ${!n.isRead ? "bg-blue-50 border-l-4 border-blue-500" : ""
                  }`}
                onClick={() => handleNotificationClick(n)}
              >
                <div className="flex gap-3 sm:gap-4">
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center ${!n.isRead
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-600'
                      }`}>
                      {getNotificationIcon(n.type)}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${n.type === 'NEW_ORDER' ? 'bg-green-100 text-green-800' :
                          n.type === 'ORDER_ACCEPTED' ? 'bg-blue-100 text-blue-800' :
                            n.type === 'ORDER_CANCELLED' ? 'bg-red-100 text-red-800' :
                              n.type === 'ORDER_COMPLETED' ? 'bg-purple-100 text-purple-800' :
                                'bg-gray-100 text-gray-800'
                        }`}>
                        {n.type.replace("_", " ")}
                      </span>
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <FiClock size={10} className="w-3 h-3 sm:w-4 sm:h-4" />
                        {formatTimeAgo(n.createdAt)}
                      </span>
                    </div>

                    <h4 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">{n.title}</h4>
                    <p className="text-gray-600 text-sm line-clamp-2">{n.message}</p>

                    {/* Order Details */}
                    {n.data && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {n.data.orderType && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                            <FiPackage size={10} className="w-3 h-3 sm:w-4 sm:h-4" />
                            {n.data.orderType}
                          </span>
                        )}
                        {n.data.totalAmount && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                            ₹{n.data.totalAmount}
                          </span>
                        )}
                        {n.data.tableId && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                            Table #{n.data.tableId}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex-shrink-0">
                    {!n.isRead ? (
                      <button
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200 hover:scale-110"
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(n._id);
                        }}
                        title="Remove notification"
                      >
                        <FiCheck size={14} className="w-4 h-4" />
                      </button>
                    ) : (
                      <div className="p-2 text-green-600">
                        <FiCheckCircle size={14} className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationManagement;
